<template>
  <div class="space-y-6">
    <div class="space-y-1">
      <p
        class="text-foreground text-3xl md:text-[40px] font-medium leading-tight"
      >
        Current Skill Paths
      </p>
      <p class="text-muted-foreground text-base md:text-[20px]">
        A list of your current skill paths.
      </p>
    </div>

    <div v-if="loading" class="text-center py-10">
      <Loader />
    </div>

    <div v-else>
      <div v-if="learningGoals.length === 0" class="text-center py-10">
        <p class="text-gray-500 text-[13px] mb-4">
          No skill paths found. Create your first skill path to get started!
        </p>
        <button
          type="button"
          @click="navigateToCreate"
          class="inline-flex items-center gap-2 bg-black text-primaryLight text-[13px] font-medium px-6 py-3 rounded-full h-[42px]"
        >
          <span>Create a new Skill Path</span>
        </button>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="(goal, idx) in learningGoals"
          :key="goal.project_id"
          class="relative overflow-hidden rounded-[12px] border border-border bg-card p-5 shadow-sm transition-all flex flex-col h-full"
          :class="[
            isProcessing(goal.status)
              ? 'opacity-70 cursor-not-allowed'
              : 'cursor-pointer hover:shadow-md',
            isFailed(goal.status)
              ? 'border-red-300 dark:border-red-900/50 hover:border-red-400'
              : 'border-border',
          ]"
          @click="onGoalClick(goal)"
        >
          <div
            class="absolute left-0 top-0 h-full w-[10px]"
            :style="{ backgroundColor: goalAccentColor(goal, idx) }"
          ></div>

          <div class="pl-3 flex-1 flex flex-col">
            <div class="flex-1">
              <p
                class="text-lg md:text-[20px] font-semibold text-foreground truncate"
              >
                {{ goal.topic }}
              </p>
              <p
                class="text-xs md:text-[13px] text-muted-foreground mt-2 line-clamp-2"
              >
                {{
                  goal.description ||
                  "Usually helps you to become a wizard in frontend"
                }}
              </p>
            </div>

            <div class="mt-4 pt-2">
              <div class="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  class="h-full rounded-full transition-all"
                  :style="{
                    width: `${goalProgressPercent(goal)}%`,
                    backgroundColor: getStatusColor(goal.status).progressBg,
                  }"
                ></div>
              </div>

              <div
                class="mt-3 flex items-center justify-between text-[12px] text-muted-foreground"
              >
                <span>{{ goalProgressPercent(goal) }}% complete</span>
                <span class="inline-flex items-center gap-1">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.8346 7.00033C12.8346 10.2203 10.2213 12.8337 7.0013 12.8337C3.7813 12.8337 1.16797 10.2203 1.16797 7.00033C1.16797 3.78033 3.7813 1.16699 7.0013 1.16699C10.2213 1.16699 12.8346 3.78033 12.8346 7.00033Z"
                      stroke="#ABABB3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M9.16516 8.85503L7.35682 7.77586C7.04182 7.58919 6.78516 7.14003 6.78516 6.77253V4.38086"
                      stroke="#ABABB3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  {{ goalLessonsLeft(goal) }} lessons left
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";
import Loader from "../ui/Loader.vue";

export default {
  name: "LearningGoalsAllComponent",
  components: {
    Loader,
  },
  data() {
    return {
      learningGoals: [],
      loading: true,
    };
  },
  methods: {
    navigateToCreate() {
      this.$router.push({ name: "CreateLearningGoal" });
    },
    onGoalClick(goal) {
      if (this.isProcessing(goal?.status)) {
        this.$toast?.info?.(
          "Processing",
          "This learning goal is still being generated. Please wait a moment.",
        );
        return;
      }

      this.$router.push({
        name: "LearningGoalDetails",
        params: { id: goal.project_id },
      });
    },
    isProcessing(status) {
      return (status || "").toString().toLowerCase() === "processing";
    },
    isFailed(status) {
      return (status || "").toString().toLowerCase() === "failed";
    },
    goalProgressPercent(goal) {
      const raw = goal?.progress;
      const value = typeof raw === "number" ? raw : Number(raw);
      if (!Number.isFinite(value)) return 0;
      return Math.max(0, Math.min(100, Math.round(value)));
    },
    goalLessonsLeft(goal) {
      return goal?.lessons_left ?? goal?.lessons_count ?? 0;
    },
    goalAccentColor(goal, idx) {
      const status = goal?.status?.toLowerCase?.() || "";
      if (status === "completed") return "#D8F5E0";
      if (status === "failed") return "#FEE2E2";
      if (status === "processing") return "#E0E7FF";
      if (status === "active" || status === "in_progress") return "#FBE7D2";
      return idx % 2 === 0 ? "#D8F5E0" : "#FBE7D2";
    },
    getStatusColor(status) {
      switch ((status || "").toString().toLowerCase()) {
        case "completed":
          return {
            progressBg: "#16A34A",
          };
        case "processing":
          return {
            progressBg: "#6366F1",
          };
        case "failed":
          return {
            progressBg: "#EF4444",
          };
        case "in_progress":
        case "active":
          return {
            progressBg: "#D97706",
          };
        default:
          return {
            progressBg: "#3D3D3D",
          };
      }
    },
    async fetchLearningGoals() {
      try {
        this.loading = true;
        const response = await apiClient.get(
          "/web/learning-goals/my-learning-goals",
        );
        this.learningGoals = response.data || [];
      } catch (error) {
        console.error("Error fetching learning goals:", error);
        this.learningGoals = [];
        this.$toast?.error?.(
          "Loading Failed",
          "Failed to load learning goals. Please try again.",
        );
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetchLearningGoals();
  },
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
